import type { Game } from 'boardgame.io'
import { MCTSBot } from 'boardgame.io/ai'
import { InitializeGame, ProcessGameConfig } from 'boardgame.io/internal'
import type { Bot, BotProgress } from './bot'

if (typeof window !== 'undefined' && !(window as any).setImmediate) {
  ;(window as any).setImmediate = (callback: (...args: any[]) => void, ...args: any[]) => setTimeout(callback, 0, ...args)
}

/** Runs Boardgame.io's MCTS bot and converts its action into a Merels move. */
export class MCTSGameBot implements Bot {
  private bot: MCTSBot | null = null

  constructor(
    private game: Game,
    private iterations = 200,
    private playoutDepth = 20,
  ) {}

  private getBot() {
    if (this.bot) return this.bot

    this.bot = new MCTSBot({
      game: this.game,
      enumerate: this.game.ai?.enumerate as any,
      objectives: (this.game.ai as any)?.objectives,
      iterations: this.iterations,
      playoutDepth: this.playoutDepth,
    })
    this.bot.setOpt('async', true)
    return this.bot
  }

  async *chooseNextMove(gameState: any, ctx: any, playerID: string): AsyncGenerator<BotProgress, any, void> {
    const game = ProcessGameConfig(this.game)
    const baseState = InitializeGame({ game, numPlayers: ctx.numPlayers || 2, setupData: {} })
    const state = {
      ...baseState,
      G: structuredClone(gameState),
      ctx: { ...baseState.ctx, ...ctx },
    }

    yield { percentComplete: 0, message: `Evaluating ${this.iterations} positions.` }
    const result = await this.getBot().play(state, playerID)
    const payload = result.action?.payload
    if (!payload) throw new Error('Bot did not return a valid move')

    const move = { type: payload.type, args: payload.args || [] }
    yield { percentComplete: 100, message: 'Move selected.', result: move }
    return move
  }
}

/** Creates a configurable MCTS bot for a Boardgame.io game with an AI enumerator. */
export function createMCTSBot(game: Game, iterations?: number, playoutDepth?: number): Bot {
  return new MCTSGameBot(game, iterations, playoutDepth)
}
